/** Partenaire Pro — entrée JSON / API. */
export interface ProPartnerRaw {
  readonly schoolname?: string | null
  readonly schoollogourl?: string | null
}

/** Partenaire Pro normalisé pour l'UI. */
export interface ProPartner {
  readonly schoolname: string
  readonly schoollogourl: string | null
}

export type ProPartnersPayload = readonly ProPartnerRaw[]

export type ProPartnersNormalized = readonly ProPartner[]

/** État vide avant chargement du fallback. */
export const EMPTY_PRO_PARTNERS: ProPartnersNormalized = []

export const DEFAULT_PRO_PARTNERS_FALLBACK_FILE: string = 'pro-partners-fallback.json'

/**
 * Indique si la valeur est un tableau non vide.
 * @param {T[]|null|undefined} v - Valeur à tester.
 * @returns {boolean} `true` si `v` est un tableau avec au moins un élément.
 */
function isNonEmptyArray<T>(v: readonly T[] | null | undefined): v is readonly T[] {
  return Array.isArray(v) && v.length > 0
}

/**
 * Clone profond d'une liste de partenaires Pro.
 * @param {ProPartnersNormalized} data - Liste source à cloner.
 * @returns {ProPartnersNormalized} Copie indépendante des partenaires.
 */
export function cloneProPartners(data: ProPartnersNormalized): ProPartnersNormalized {
  return structuredClone(data)
}

/**
 * Normalise une liste brute de partenaires Pro.
 * @param {ProPartnerRaw[]|null|undefined} list - Entrées JSON / API brutes.
 * @returns {ProPartnersNormalized} Partenaires valides avec `schoolname` et `schoollogourl` typés.
 */
function normalizePartners(
  list: readonly ProPartnerRaw[] | null | undefined,
): ProPartnersNormalized {
  if (!isNonEmptyArray(list)) return []

  return list
    .map((item: ProPartnerRaw): ProPartner | null => {
      const schoolname: string = item.schoolname?.trim() ?? ''
      if (!schoolname) return null

      const schoollogourlRaw: string = item.schoollogourl?.trim() ?? ''
      const schoollogourl: string | null = schoollogourlRaw || null

      return { schoolname, schoollogourl }
    })
    .filter((item: ProPartner | null): item is ProPartner => item !== null)
}

/**
 * Fusionne un `ProPartnersPayload` : le tableau API/JSON non vide remplace la base ;
 * résultat normalisé vide → on conserve la base (évite d'écraser le fallback).
 * @param {ProPartnersNormalized} base - Données de départ (`EMPTY_PRO_PARTNERS` ou déjà fusionné depuis le JSON).
 * @param {ProPartnersPayload|null|undefined} raw - JSON et/ou fragment API.
 * @returns {ProPartnersNormalized} Partenaires fusionnés.
 */
export function mergeProPartners(
  base: ProPartnersNormalized,
  raw: ProPartnersPayload | null | undefined,
): ProPartnersNormalized {
  if (!raw) return base

  const normalized: ProPartnersNormalized = normalizePartners(raw)
  if (normalized.length === 0) return base

  return normalized
}
